﻿import {translation} from "@2d-front-monorepo/core";
import {LinkButton, Message, TabPanel, Tabs} from "@2d-front-monorepo/design-library";
import {
	GroupedPermit,
	InstallationByHostData, IPermitEntityResult,
	PermitResult,
	Permits,
	ReservationResult
} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";

import {JSX, useMemo} from "react";
import AddCreditCardMessage from "../../components/AddCreditCardMessage";
import MaterialIcon from "../../components/MaterialIcon";
import TopUserInfo from "../../components/TopUserInfo";
import {PermitsState} from "../../contexts/PermitsContext";
import Registrations from "../../customers/Registrations";
import {getGlobalContext} from "../../hooks/getGlobalContext";
import {usePermits} from "../../hooks/usePermits";
import {PrivateRoutes} from "../../routes";
import ArchivedPermits from "./ArchivedPermits";
import ArchivedReservations from "./ArchivedReservations";
import PermitListComponent from "./ViewComponents/PermitListComponent";
import ReservationListComponent from "./ViewComponents/ReservationListComponent";

type FilteredPermitsProps = PermitsState & {
	installation: InstallationByHostData;
	filtered: PermitResult[];
	topMenu: JSX.Element;
}

type FilteredReservationsProps = PermitsState & {
	installation: InstallationByHostData;
	filtered: ReservationResult[];
	topMenu: JSX.Element;
}

function filterPermitsByTariffs<T extends IPermitEntityResult<any>>(permits: T[], tariffIds: string[]): T[] {
	return permits.filter(permit => tariffIds.includes(permit.tariffId));
}

function getUngroupedPermits(permits: PermitResult[]): GroupedPermit[] {
	return permits.map(x => {
		return {
			// One card per permit: only its own vehicles/attachments feed the card's
			// plate list and change/expiry indicators (mirrors Permits.getGroupedPermits).
			permits: [x],
			permit: x,
			name: "",
			count: 1,
			startDate: x.startDate,
			endDate: x.endDate
		}
	});
}

function FilteredPermits(props: FilteredPermitsProps): JSX.Element {
	const {installation, filtered, topMenu} = props;

	const grouped = installation.configuration.groups.enabled
		? Permits.getGroupedPermits(filtered)
		: getUngroupedPermits(filtered);

	return <>
		<AddCreditCardMessage/>
		<div className="form__buttons form__buttons--top">
			<LinkButton to={PrivateRoutes.permitsHome} small={true}>
				<MaterialIcon icon="add"/>
				{translation(PermitTranslations.summary_page__add_new_permit)}
			</LinkButton>
			{topMenu}
		</div>
		<PermitListComponent {...props} grouped={grouped} installation={installation}/>
	</>;
}

function FilteredReservations(props: FilteredReservationsProps): JSX.Element {
	const {installation, topMenu} = props;

	return <>
		<AddCreditCardMessage/>
		<div className="form__buttons form__buttons--top">
			{topMenu}
		</div>
		<ReservationListComponent {...props} installation={installation}/>
	</>;
}

type TabData = {
	id: string;
	title: string;
	content: JSX.Element;
}

export default function PermitList(): JSX.Element {
	const permitsState = usePermits();
	const {installation} = getGlobalContext();
	const PermitTopMenuComponent = Registrations.getPermitsTopMenu();
	const ReservationTopMenuComponent = Registrations.getReservationsTopMenu();

	const hasPermits = permitsState.permits.length > 0 || permitsState.hasArchivedPermits;
	const hasReservations = permitsState.reservations.length > 0 || permitsState.hasArchivedReservations;
	const isEmpty = !hasPermits && !hasReservations;

	const tabs = useMemo<TabData[]>(() => {
		const result: TabData[] = [];
		// Permits whose tariff matches no configured group must still show somewhere:
		// leftovers land in the generic tab instead of silently disappearing.
		const groupedPermits = new Set<PermitResult>();
		for (const [key, tariffIds] of Object.entries(installation.configuration.groups.permits)) {
			const filtered = filterPermitsByTariffs(permitsState.permits, tariffIds);
			if (filtered.length == 0) {
				continue
			}

			filtered.forEach(p => groupedPermits.add(p));
			result.push({
				id: key,
				title: translation(key),
				content: <FilteredPermits {...permitsState}
				                          installation={installation}
				                          filtered={filtered}
				                          topMenu={<PermitTopMenuComponent permits={permitsState}/>}
				/>
			});
		}

		const ungroupedPermits = permitsState.permits.filter(p => !groupedPermits.has(p));
		if (ungroupedPermits.length > 0) {
			result.push({
				id: "active",
				title: translation(PermitTranslations.summary_page__permits),
				content: <FilteredPermits {...permitsState}
				                          installation={installation}
				                          filtered={ungroupedPermits}
				                          topMenu={<PermitTopMenuComponent permits={permitsState}/>}/>
			});
		}

		const groupedReservations = new Set<ReservationResult>();
		for (const [key, tariffIds] of Object.entries(installation.configuration.groups.reservations)) {
			const filtered = filterPermitsByTariffs(permitsState.reservations, tariffIds);
			if (filtered.length == 0) {
				continue
			}

			filtered.forEach(r => groupedReservations.add(r));
			result.push({
				id: key,
				title: translation(key),
				content: <FilteredReservations {...permitsState}
				                               installation={installation}
				                               filtered={filtered}
				                               topMenu={<ReservationTopMenuComponent reservations={permitsState}/>}
				/>
			});
		}

		const ungroupedReservations = permitsState.reservations.filter(r => !groupedReservations.has(r));
		if (ungroupedReservations.length > 0) {
			result.push({
				id: "reservations",
				title: translation(PermitTranslations.summary_page__reservations),
				content: <FilteredReservations {...permitsState}
				                               installation={installation}
				                               filtered={ungroupedReservations}
				                               topMenu={<ReservationTopMenuComponent reservations={permitsState}/>}/>
			});
		}

		if (permitsState.hasArchivedPermits) {
			result.push({
				id: "archived-permits",
				title: translation(PermitTranslations.summary_page__archived__permits),
				content: <ArchivedPermits/>
			});
		}

		if (permitsState.hasArchivedReservations) {
			result.push({
				id: "archived-reservations",
				title: translation(PermitTranslations.summary_page__archived__reservations),
				content: <ArchivedReservations/>
			});
		}

		return result;
	}, [permitsState, installation, hasPermits, hasReservations]);

	return (
		<>
			<TopUserInfo/>
			<h1>{translation(PermitTranslations.summary_page__title)}</h1>
			{isEmpty && <>
                <hr/>
                <Message title={{text: translation(PermitTranslations.summary_page__no_permits)}}
                         type="information"/></>}
			{(hasPermits || hasReservations) &&
                <Tabs>
					{tabs.map(tab => (
						<TabPanel key={tab.id} title={tab.title}>
							{tab.content}
						</TabPanel>
					))}
                </Tabs>
			}
		</>
	);
}
